import { ChevronDown, ExternalLink, Plug } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Switch } from "~/components/ui/switch";

export interface FakeConnector {
  id: string;
  name: string;
  description: string;
  vendor_url?: string;
}

export const FAKE_CONNECTORS: FakeConnector[] = [
  {
    id: "snowflake",
    name: "Snowflake",
    description: "Query Snowflake warehouses attached to this project.",
    vendor_url: "https://www.snowflake.com",
  },
  {
    id: "postgres",
    name: "PostgreSQL",
    description: "Read from connected application databases.",
    vendor_url: "https://www.postgresql.org",
  },
  {
    id: "opensearch",
    name: "OpenSearch",
    description: "Full-text search across the project's indexes.",
    vendor_url: "https://opensearch.org",
  },
  {
    id: "notion",
    name: "Notion",
    description: "Read pages from connected Notion workspaces.",
    vendor_url: "https://www.notion.so",
  },
];

interface ConnectorsPopoverProps {
  connectors: FakeConnector[];
  enabledIds: string[];
  onToggle: (id: string) => void;
}

export const ConnectorsPopover = ({ connectors, enabledIds, onToggle }: ConnectorsPopoverProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs text-muted-foreground"
        >
          <Plug className="size-3.5" />
          Connectors ({enabledIds.length})
          <ChevronDown className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0">
        <div className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Connectors</p>
          <p className="text-xs text-muted-foreground">
            Choose which connectors this conversation can use.
          </p>
        </div>

        <div className="mx-3 mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          TODO: connectors are placeholders. Toggling here doesn&apos;t affect agent behavior yet.
        </div>

        <ul className="flex max-h-80 flex-col divide-y overflow-y-auto px-3 py-2" role="list">
          {connectors.map((connector) => {
            const enabled = enabledIds.includes(connector.id);
            return (
              <li key={connector.id} className="flex items-center justify-between gap-4 py-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">{connector.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {connector.description}
                    {connector.vendor_url && (
                      <>
                        {" "}
                        <a
                          href={connector.vendor_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 underline hover:text-foreground"
                        >
                          Learn more
                          <ExternalLink className="size-3" />
                        </a>
                      </>
                    )}
                  </span>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={() => onToggle(connector.id)}
                  aria-label={`Toggle ${connector.name}`}
                />
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
